<template>
    <div id="resourceSearch">
        <div class="container" v-show="resources.length === 0">
            <span>Loading...</span>
        </div>
        <div class="container" v-show="resources.length > 0">
            <div class="row">
                <div class="col-lg-3 search-col">
                    <section role="search" class="sticky-top" id="search-col-container">
                        <h2 class="search-header">Filter Resources</h2>
                        <div class="container-fluid filter-body">
                            <div class="row search-row">
                                <div class="col-12">
                                    <label for="resource-search-category">
                                        Category Filter
                                        <b-icon-info-fill v-b-tooltip.hover.focus title="Choose one or more service categories" font-scale="1.5" variant="primary" tabindex="0"></b-icon-info-fill>
                                    </label>
                                    <multiselect
                                            v-model="selectedCategories"
                                            id="resource-search-category"
                                            :options="searchCats"
                                            :multiple="true"
                                            :close-on-select="false"
                                            :clear-on-select="false"
                                            :preserve-search="true"
                                            label="title"
                                            track-by="idCategory"
                                            :preselect-first="false"
                                            :show-labels="false"
                                            placeholder=""
                                            :internal-search="false"
                                            @search-change="catSearch"
                                    >
                                        <template slot="option" slot-scope="props">
                                            <div class="option__desc">
                                                <span class="option__title">{{props.option.title}}</span>
                                                <span class="option__small">{{props.option.description}}</span>
                                            </div>
                                        </template>
                                    </multiselect>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-12">
                                    <label for="resource-search-subpop">
                                        Sub-Population Filter
                                        <b-icon-info-fill v-b-tooltip.hover.focus title="Choose one or more that best describes who you are working with" font-scale="1.5" variant="primary" tabindex="0"></b-icon-info-fill>
                                    </label>
                                    <multiselect
                                            v-model="selectedSubpops"
                                            id="resource-search-subpop"
                                            :options="subpops"
                                            :multiple="true"
                                            :close-on-select="false"
                                            :clear-on-select="false"
                                            label="title"
                                            track-by="title"
                                            :preselect-first="false"
                                            :show-labels="false"
                                            placeholder="">
                                    </multiselect>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-12">
                                    <label for="resource-search-payer">
                                        Insurance Type Filter
                                        <b-icon-info-fill v-b-tooltip.hover.focus title="Choose the type of insurance your client has. Results will show by both insurance type and no-cost services." font-scale="1.5" variant="primary" tabindex="0"></b-icon-info-fill>
                                    </label>
                                    <multiselect
                                            v-model="selectedPayers"
                                            id="resource-search-payer"
                                            :options="payers"
                                            :multiple="true"
                                            :close-on-select="false"
                                            :clear-on-select="false"
                                            label="title"
                                            track-by="title"
                                            :preselect-first="false"
                                            :show-labels="false"
                                            placeholder="">
                                    </multiselect>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-12">
                                    <label for="resource-search-age">
                                        Age Filter
                                        <b-icon-info-fill v-b-tooltip.hover.focus title="Choose how old your client is. Results will show by both specific and no age requirements." font-scale="1.5" variant="primary" tabindex="0"></b-icon-info-fill>
                                    </label>
                                    <multiselect
                                            v-model="selectedAges"
                                            id="resource-search-age"
                                            :options="ages"
                                            :multiple="true"
                                            :close-on-select="false"
                                            :clear-on-select="false"
                                            label="title"
                                            track-by="title"
                                            :preselect-first="false"
                                            :show-labels="false"
                                            placeholder="">
                                    </multiselect>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-12">
                                    <GeoSearch v-model="geoSearchText" @updateplace="geoPlace = $event"></GeoSearch>
                                </div>
                                <div class="col-6 push-right">
                                    <div class="select-wrapper">
                                        <select class="form-control" v-bind:class="{'input-placeholder': !geoRadiusComp}" v-bind:disabled="!geoRadiusComp" v-model="geoRadiusComp" aria-label="Geographic Radius" id="geo-radius">
                                            <option v-bind:value="null" disabled>Radius</option>
                                            <option value="5">5 Miles</option>
                                            <option value="10">10 Miles</option>
                                            <option value="25">25 Miles</option>
                                            <option value="50">50 Miles</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-12">
                                    <span class="small float-right">{{resourcesSearch.length}} Resources Found</span>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-md-12">
                                    <button type="button" class="btn btn-primary btn-sm float-right" @click="resetSearchCriteria()">
                                        Reset Search
                                    </button>
                                </div>
                            </div>
                            <div class="row search-row" v-if="shareActive">
                                <div class="col-12">
                                    <label>Share or Print</label>
                                    <small>Check the checkboxes for the resources you would like to share or print.</small>
                                    <div id="share-list">
                                        <div class="share-list-resource" v-for="shareRes in shareResources" :key="shareRes">
                                            <div class="share-list-resource-name">
                                                {{resourcesByID[shareRes].name}}
                                            </div>
                                            <div class="share-list-resource-button">
                                                <button type="button" @click="removeShareRes(shareRes)"><span aria-hidden="true">&times;</span></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="row search-row">
                                <div class="col-md-12" v-show="!shareActive">
                                    <button type="button" class="btn btn-primary btn-sm float-right" @click="shareActive = true">
                                        Share/Print
                                    </button>
                                </div>
                                <div class="col-md-12 share-buttons" v-show="shareActive">
                                    <div class="input-group" id="share-url-group">
                                        <input type="text" ref="shareUrlInput" class="form-control form-control-sm" readonly :value="shareURL">
                                        <button type="button" class="btn btn-primary btn-sm" @click="copyShareUrl()">
                                            Copy Link
                                        </button>
                                    </div>
                                    <button type="button" class="btn btn-primary btn-sm float-right" @click="shareActive = false">
                                        Cancel
                                    </button>
                                    <a :href="printShareURL" class="btn btn-primary btn-sm float-right">
                                        Print
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
                <div class="col" id="search-list-container">
                    <main role="main">
                        <div class="main-header container sticky-top">
                            <div class="row">
                                <div class="col-md-8">
                                    <h2 class="search-header">Resource List</h2>
                                </div>
                                <div class="col-md-4">
                                    <div class="input-group resource-search">
                                        <input type="text" class="form-control form-control-sm" id="resource-list-search-new" placeholder="Text Search..." v-model="searchText" v-on:focusout="trackTextSearch()" maxlength="100">
                                        <button type="button" class="close" style="width:35px; margin-left: -35px; z-index: 100;" v-on:click="clearSearch()"><span aria-hidden="true">&times;</span></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="main-resources container">
                            <div class="row">
                                <div class="col-12">
                                    <div class="detail-block no-print" v-for="resource in resourcesPage" v-bind:key="resource.idResource">
                                        <div class="detail-header">
                                            <div class="custom-control custom-checkbox" v-if="shareActive">
                                                <input type="checkbox" class="custom-control-input" :id="'share-' + resource.idResource" :value="resource.idResource" v-model="shareResources">
                                                <label class="custom-control-label" :for="'share-' + resource.idResource">
                                                    Include in Share
                                                </label>
                                            </div>
                                            <h3>{{resource.name}}</h3>
                                        </div>
                                        <div class="detail-body container-fluid">
                                            <div class="row">
                                                <div class="detail-desc col-12">
                                                    {{resource.description}}
                                                </div>
                                                <div class="detail-addr col-sm">
                                                    <div class="detail-body-subhead" >
                                                        <span v-if="resource.addressNote">{{resource.addressNote}}</span>
                                                        <span v-else>Address</span>
                                                    </div>
                                                    <div class="detail-addr-body" v-if="resource.physicalAddress == 1 || resource.physicalAddress == 2">{{resource.address}}<br />{{resource.city}}, {{resource.state}} {{resource.zip}}</div>
                                                </div>
                                                <div class="detail-phone col-sm" v-if="resource.phone">
                                                    <div class="detail-body-subhead">Phone</div>
                                                    <div>{{resource.phone | phoneNum}}</div>
                                                </div>
                                                <div class="detail-found col-12" v-show="filtersActive()">
                                                    <div class="detail-body-subhead">Found Filters</div>
                                                    <div class="detail-found-cats" v-if="selectedCategories.length > 0">
                                                        Categories: {{getCategoriesString(resource.idCategories)}}
                                                    </div>
                                                    <div class="detail-found-subpops" v-if="selectedSubpops.length > 0">
                                                        Sub-Populations: {{getSubpopsString(resource.idSubpops)}}
                                                    </div>
                                                    <div class="detail-found-payers" v-if="selectedPayers.length > 0">
                                                        Payers: {{getPayersString(resource.idPayers)}}
                                                    </div>
                                                    <div class="detail-found-payers" v-if="selectedAges.length > 0">
                                                        Ages: {{getAgesString(resource.idAges)}}
                                                    </div>
                                                </div>
                                                <div class="detail-links col-sm">
                                                    <a v-show="resource.website" class="btn btn-sm btn-secondary" v-bind:href="urlify(resource.website)" target="_blank" :aria-label="resource.name + ' website'">Website Link</a>
                                                    <router-link v-bind:to="'/detail/' + resource.idResource" class="btn btn-sm btn-primary">More Details</router-link>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                    <div class="detail-block print-only" v-for="resource in resourcesSearch" v-bind:key="resource.idResource + 'print'">
                                        <div class="detail-header">
                                            <h3>{{resource.name}}</h3>
                                        </div>
                                        <div class="detail-body container-fluid">
                                            <div class="row">
                                                <div class="detail-desc col-12">{{resource.description}}</div>
                                                <div class="detail-addr col-sm">
                                                    <div class="detail-body-subhead">
                                                        <span v-if="resource.addressNote">{{resource.addressNote}}</span>
                                                        <span v-else>Address</span>
                                                    </div>
                                                    <div class="detail-addr-body" v-if="resource.physicalAddress == 1 || resource.physicalAddress == 2">{{resource.address}}<br />{{resource.city}}, {{resource.state}} {{resource.zip}}</div>
                                                </div>
                                                <div class="detail-phone col-sm" v-if="resource.phone">
                                                    <div class="detail-body-subhead">Phone</div>
                                                    <div>{{resource.phone | phoneNum}}</div>
                                                </div>
                                                <div class="detail-links col-12" v-if="resource.website">
                                                    <div class="detail-body-subhead">
                                                        Website
                                                    </div>
                                                    <div>{{resource.website}}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                    <nav>
                        <ul class="pagination">
                            <li class="page-item" v-bind:class="{disabled: (this.$store.state.pageNumber === 0)}"><button class="page-link" @click="prevPage()">Previous</button></li>
                            <li class="page-item"><span class="page-info">{{currPages}}</span></li>
                            <li class="page-item" v-bind:class="{disabled: (this.$store.state.pageNumber === Math.floor(resourcesSearch.length/this.$store.state.pageSize))}"><button class="page-link" @click="nextPage()">Next</button></li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
        <CrisisScreening @user-in-crisis="setCrisisFilters"></CrisisScreening>
    </div>
</template>

<script>
    import axios from 'axios';
    import _ from 'lodash';
    import fuzzysort from 'fuzzysort';
    import GeoSearch from '@/components/GeoSearch'
    import CrisisScreening from "@/components/CrisisScreening";
    import _debounce from 'lodash/debounce'

    export default {
        name: "ResourceSearch",
        components: {
            CrisisScreening,
            GeoSearch
        },
        data () {
            return {
                resources: [],

                searchText: this.$store.state.textSearch,
                textResources: [],

                //used for searching on sub population
                subpops: [],
                selectedSubpops: this.$store.state.selectedSubpops,
                subpopResources: [],

                //used for searching on category
                categories: [],
                selectedCategories: this.$store.state.selectedCategories,
                categoryResources: [],
                searchCats: [],
                testing: [],

                //used for searching on payer type
                payers: [],
                selectedPayers: this.$store.state.selectedPayers,
                payerResources: [],

                //used for searching on payer type
                ages: [],
                selectedAges: this.$store.state.selectedAges,
                ageResources: [],

                //geoSearchText
                geoSearchText: this.$store.getters.geoSearchText,
                geoPlace: this.$store.state.geoPlace,
                geoRadius: this.$store.state.geoRadius,
                geoResults: [],

                //if initial load don't reset page
                initialText: true,
                initialSub: true,
                initialCat: true,
                initialPay: true,
                initialGeo: true,
                initialGeoRad: true,

                shareActive: this.$store.state.shareActive,
                shareResources: this.$store.state.shareResources,

                shareTranslateString: ""
            }
        },
        mounted (){
            this.setupGTListener();

            axios.get("/api/resource.php")
                .then(response => {
                    if (response.data){
                        this.resources = response.data;
                        this.$nextTick(() =>{
                            this.$root.$emit('resourcesLoaded');
                        });
                    }
                });

            axios.get("/api/subpop.php")
                .then(response => {
                    if (response.data){
                        this.subpops = response.data;
                    }
                });

            axios.get("/api/category.php")
                .then(response => {
                    if (response.data){
                        this.categories = response.data;
                        this.searchCats = response.data;
                    }
                });

            axios.get("/api/payer.php")
                .then(response => {
                    if (response.data){
                        this.payers = response.data;
                    }
                });

            axios.get("/api/age.php")
                .then(response => {
                    if (response.data){
                        this.ages = response.data;
                    }
                });
        },
        watch: {
            searchText: {
                immediate: 'true',
                handler (val){
                    if(this.initialText){
                        this.initialText = false;
                    } else {
                        this.$store.commit('resetPage');
                    }
                    this.$store.commit('setTextSearch', val);

                    this.doTextSearch()
                }
            },
            selectedSubpops: {
                immediate: 'true',
                handler (val){
                    //don't reset page on initial load
                    if(this.initialSub){
                        this.initialSub = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save the new selected subpopulation search
                    this.$store.commit('setSelectedSubpops', val);

                    //gets ids to search on
                    let idCriteriaOptions = [];
                    val.forEach(obj =>{
                        idCriteriaOptions.push(obj.idCriteriaOption);
                        //this.$ga.event("search", "subpop", obj.title)
                        this.$gtag.event("subpop", {'event_category': 'search', 'value': obj.title})
                    });

                    //sends request for resources that have selected subpop
                    if(idCriteriaOptions.length > 0){
                        let data = {idCriteriaOptions: idCriteriaOptions};
                        axios.post('/api/subpop.php', data).then(response => {
                            let r = [];
                            response.data.forEach(obj =>{
                                r.push(obj);
                            });

                            this.subpopResources = r;
                        })
                    } else {
                        this.subpopResources = [];
                    }
                }
            },
            selectedCategories: {
                immediate: 'true',
                handler (val){
                    //don't reset page on initial load
                    if(this.initialCat){
                        this.initialCat = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save new selected categories
                    this.$store.commit('setSelectedCategories', val);

                    //get ids to search on
                    let idCategories = [];
                    val.forEach(obj =>{
                        idCategories.push(obj.idCategory);
                        //this.$ga.event("search", "category", obj.title + " " + (obj.description || ""))
                        this.$gtag.event("category", {'event_category': 'search', 'value': obj.title + " " + (obj.description || "")})

                    });

                    //sends request for resources with selected category ids
                    if(idCategories.length > 0){
                        let data = {idCategories: idCategories};
                        axios.post('/api/category.php', data).then(response => {
                            let r = [];
                            response.data.forEach(obj =>{
                                r.push(obj);
                            });

                            this.categoryResources = r;
                        })
                    } else {
                        this.categoryResources = [];
                    }
                }
            },
            selectedPayers: {
                immediate: 'true',
                handler (val){
                    //don't reset page on initial load
                    if(this.initialPay){
                        this.initialPay = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save the new selected subpopulation search
                    this.$store.commit('setSelectedPayers', val);

                    //gets ids to search on
                    let idCriteriaOptions = [];
                    val.forEach(obj =>{
                        idCriteriaOptions.push(obj.idCriteriaOption);
                        //this.$ga.event("search", "insurance", obj.title)
                        this.$gtag.event("insurance", {'event_category': 'search', 'value': obj.title})

                    });

                    //sends request for resources that have selected subpop
                    if(idCriteriaOptions.length > 0){
                        let data = {idCriteriaOptions: idCriteriaOptions};
                        axios.post('/api/payer.php', data).then(response => {
                            let r = [];
                            response.data.forEach(obj =>{
                                r.push(obj);
                            });

                            this.payerResources = r;
                        })
                    } else {
                        this.payerResources = [];
                    }
                }
            },
            selectedAges: {
                immediate: 'true',
                handler (val){
                    //don't reset page on initial load
                    if(this.initialPay){
                        this.initialPay = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save the new selected subpopulation search
                    this.$store.commit('setSelectedAges', val);

                    //gets ids to search on
                    let idCriteriaOptions = [];
                    val.forEach(obj =>{
                        idCriteriaOptions.push(obj.idCriteriaOption);
                        //this.$ga.event("search", "age", obj.title)
                        this.$gtag.event("age", {'event_category': 'search', 'value': obj.title})

                    });

                    //sends request for resources that have selected subpop
                    if(idCriteriaOptions.length > 0){
                        let data = {idCriteriaOptions: idCriteriaOptions};
                        axios.post('/api/age.php', data).then(response => {
                            let r = [];
                            response.data.forEach(obj =>{
                                r.push(obj);
                            });

                            this.ageResources = r;
                        })
                    } else {
                        this.ageResources = [];
                    }
                }
            },
            geoPlace: {
                immediate: 'true',
                handler (val){
                    if(this.initialGeo){
                        this.initialGeo = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save the new selected geoPlace
                    this.$store.commit('setGeoPlace', val);
                    this.geoSearchText = this.$store.getters.geoSearchText;

                    if(val && val.geometry){
                        //this.$ga.event("search", "location", val.formatted_address);
                        this.$gtag.event("location", {'event_category': 'search', 'value': val.formatted_address})


                        let params = {
                            params: {
                                lat: val.geometry.location.lat(),
                                lng: val.geometry.location.lng(),
                                rad: this.geoRadius
                            }
                        };

                        axios.get("/api/resource.php", params)
                            .then(response => {
                                if (response.data){
                                    let r = [];
                                    response.data.forEach(obj =>{
                                        r.push(obj.idResource);
                                    });

                                    this.geoResults = r;
                                }
                            });
                    }
                }
            },
            geoRadius: {
                immediate: 'true',
                handler (val){
                    if(this.initialGeoRad){
                        this.initialGeoRad = false;
                    } else {
                        this.$store.commit('resetPage');
                    }

                    //save the new selected geoPlace
                    this.$store.commit('setGeoRadius', val);

                    if(this.geoPlace && this.geoPlace.geometry){
                        let params = {
                            params: {
                                lat: this.geoPlace.geometry.location.lat(),
                                lng: this.geoPlace.geometry.location.lng(),
                                rad: this.geoRadius
                            }
                        };

                        axios.get("/api/resource.php", params)
                            .then(response => {
                                if (response.data){
                                    let r = [];
                                    response.data.forEach(obj =>{
                                        r.push(obj.idResource);
                                    });

                                    this.geoResults = r;
                                }
                            });
                    }
                }
            },
            shareActive: {
                immediate: 'true',
                handler (val){
                    this.$store.commit('setShareActive', val);
                    if(!val){
                        this.shareResources = [];
                    }
                }
            },
            shareResources: {
                immediate: 'true',
                handler (val){
                    this.$store.commit('setShareResources', val);
                }
            }
        },
        computed: {
            shareURL(){
                let ids = this.shareResources.join(',')
                let u = window.location
                let url = u.protocol + "//" + u.host + "/share/?ids=" + ids + this.shareTranslateString;
                return url;
            },
            printShareURL(){
                let ids = this.shareResources.join(',')
                let u = window.location
                let url = u.protocol + "//" + u.host + "/share/?ids=" + ids + '&print=1' + this.shareTranslateString;
                return url;

            },
            resourcesByID (){
                let r = {}
                this.resources.forEach((res)=>{
                    r[res.idResource] = res;
                })
                return r;
            },
            resourcesSearch (){
                let currResources = this.resources;

                //filters all resources on text search
                if(this.searchText){
                    /*
                    currResources = _.filter(currResources, r =>{
                        return r.name.toLowerCase().search(this.searchText.toLowerCase()) > -1 || r.description.toLowerCase().search(this.searchText.toLowerCase()) > -1;
                    });
                    */
                    let textSearchRes = []
                    this.textResources.forEach(tr => {
                        textSearchRes.push(tr.idResource)
                    })
                    currResources = _.filter(currResources, r => {
                        return textSearchRes.indexOf(r.idResource) !== -1
                    })
                }

                //filters on selected subpopulations
                /*
                if(this.selectedSubpops.length > 0){
                    currResources = _.filter(currResources, r => {
                        return this.subpopResources.indexOf(r.idResource) !== -1
                    })
                }
                */
                let subpopLookup = {}
                this.subpopResources.forEach(subpop => {
                    subpopLookup[subpop.idResource] = subpop.idSubpops.split(",").map(c => parseInt(c))
                })
                currResources.map(res => {
                    res.idSubpops = subpopLookup[res.idResource] || [];
                })
                if(this.selectedSubpops.length > 0){
                    currResources = _.filter(currResources, r => {
                        return r.idSubpops.length > 0;
                    })
                }

                //filters on selected categories
                /*
                if(this.selectedCategories.length > 0){
                    currResources = _.filter(currResources, r => {
                        return this.categoryResources.indexOf(r.idResource) !== -1
                    })
                }
                */

                //creates mapping of resource IDs to the Category IDs the user selected
                let catresLookup = {}
                this.categoryResources.forEach(catres => {
                    catresLookup[catres.idResource] = catres.idCategories.split(",").map(c => parseInt(c))
                })
                currResources.map(res => {
                    res.idCategories = catresLookup[res.idResource] || [];
                })
                // filters out any resource that have none of the user selected categories
                if(this.selectedCategories.length > 0){
                    currResources = _.filter(currResources, r => {
                        return r.idCategories.length > 0;
                    })
                }




                //filters on selected payers
                /*
                if(this.selectedPayers.length > 0){
                    currResources = _.filter(currResources, r => {
                        return this.payerResources.indexOf(r.idResource) !== -1
                    })
                }
                 */
                let selpayerLookup = {}
                this.payerResources.forEach(selpayer => {
                    selpayerLookup[selpayer.idResource] = selpayer.idPayers.split(",").map(p => parseInt(p))
                })
                currResources.map(res => {
                    res.idPayers = selpayerLookup[res.idResource] || [];
                })
                if(this.selectedPayers.length > 0){
                    currResources = _.filter(currResources, r => {
                        return r.idPayers.length > 0;
                    })
                }

                let selageLookup = {}
                this.ageResources.forEach(selage => {
                    selageLookup[selage.idResource] = selage.idAges.split(",").map(p => parseInt(p))
                })
                currResources.map(res => {
                    res.idAges = selageLookup[res.idResource] || [];
                })
                if(this.selectedAges.length > 0){
                    currResources = _.filter(currResources, r => {
                        return r.idAges.length > 0;
                    })
                }

                //filters on geographic results
                if(this.geoPlace && this.geoPlace.geometry){
                    currResources = _.filter(currResources, r => {
                        return this.geoResults.indexOf(r.idResource) !== -1
                    })
                }

                //currResources = _.sortBy(currResources, res => -(res.idCategories.length + res.idSubpops.length))
                currResources.sort((a, b) => {
                    let aFilterCount = a.idCategories.length + a.idSubpops.length + a.idPayers.length + a.idAges.length
                    let bFilterCount = b.idCategories.length + b.idSubpops.length + b.idPayers.length + b.idAges.length
                    if(aFilterCount > bFilterCount){
                        return -1;
                    } else if(aFilterCount < bFilterCount){
                        return 1;
                    } else {
                        if(a.name < b.name){
                            return -1;
                        } else if(a.name > b.name){
                            return 1;
                        } else {
                            return 0;
                        }
                    }
                })

                return currResources
            },
            resourcesPage (){
                //paginates the filtered search results
                let start = this.$store.state.pageNumber * this.$store.state.pageSize;
                let end = start + this.$store.state.pageSize;

                return this.resourcesSearch.slice(start, end)
            },
            currPages (){
                //generates string
                //firstResult - lastResult of totalResults
                let start = this.$store.state.pageNumber * this.$store.state.pageSize;
                let end = start + this.$store.state.pageSize;
                return (start+1) + " - " + ((end < this.resourcesSearch.length) ? end : this.resourcesSearch.length) + " of " + this.resourcesSearch.length
            },
            geoRadiusComp: {
                get (){
                    return (this.geoSearchText ? this.geoRadius : null)
                },
                set (val){
                    this.geoRadius = val
                }
            }
        },
        methods: {
            doTextSearch: _debounce(function() {
                //sends request for resources that match the search text
                if(this.searchText.length > 0){
                    let params = {
                        params: {
                            search: this.searchText
                        }
                    };
                    axios.get('/api/resource.php', params).then(response => {
                        let r = [];
                        response.data.forEach(obj =>{
                            r.push(obj);
                        });

                        this.textResources = r;
                    })
                } else {
                    this.textResources = [];
                }
            }, 300),
            urlify(website){
                if(website.toLowerCase().substring(0,4) === "http"){
                    return website;
                }
                else {
                    return "//" + website;
                }
            },
            filtersActive(){
                return this.selectedSubpops.length > 0 || this.selectedCategories.length > 0 || this.selectedPayers.length > 0 || this.selectedAges.length > 0;
            },
            getCategoriesString(idCategories){
                if(!idCategories || idCategories.length === 0){
                    return "-";
                }
                let cats = this.categories.filter(c => idCategories.indexOf(c.idCategory) > -1)
                let catStrings = cats.map(c => c.title + (c.description?(" - " + c.description):""))
                return catStrings.join(", ")
            },
            getSubpopsString(idSubpops){
                if(!idSubpops || idSubpops.length === 0){
                    return "-";
                }
                let subpops = this.subpops.filter(s => idSubpops.indexOf(s.idCriteriaOption) > -1)
                let subpopStrings = subpops.map(s => s.title)
                return subpopStrings.join(", ");
            },
            getPayersString(idPayers){
                if(!idPayers || idPayers.length === 0){
                  return "-";
                }
                let payers = this.payers.filter(p => idPayers.indexOf(p.idCriteriaOption) > -1)
                let payerStrings = payers.map(p => p.title)
                return payerStrings.join(", ");
            },
            getAgesString(idAges){
                if(!idAges || idAges.length === 0){
                    return "-";
                }
                let ages = this.ages.filter(p => idAges.indexOf(p.idCriteriaOption) > -1)
                let ageStrings = ages.map(p => p.title)
                return ageStrings.join(", ");
            },
            catSearch(query){
                if(!query){
                    this.searchCats = this.categories;
                } else {
                    this.testing = fuzzysort.go(query, this.categories, {
                        threshold: -Infinity,
                        keys: ['title', 'description', 'synonyms']
                    })
                    this.searchCats = this.testing.map(res => res.obj)
                }
            },
            trackTextSearch(){
                //this.$ga.event("search", "text", this.searchText)
                this.$gtag.event("text", {'event_category': 'search', 'value': this.searchText})

            },
            resetSearchCriteria (){
                this.searchText = "";
                this.geoPlace = {};
                this.selectedPayers = [];
                this.selectedCategories = [];
                this.selectedSubpops = [];
            },
            clearSearch (){
                this.searchText = "";
            },
            nextPage() {
                let lastPage = Math.floor(this.resources.length/this.$store.state.pageSize);
                if(this.$store.state.pageNumber < lastPage){
                    this.$store.commit("nextPage");
                    this.scrollToTopOfList();
                }
            },
            prevPage() {
                if(this.$store.state.pageNumber > 0){
                    this.$store.commit("prevPage");
                    this.scrollToTopOfList();
                }
            },
            scrollToTopOfList() {
                document.getElementById("search-list-container").scrollIntoView();
            },
            printResults(){
                window.print();
            },
            removeShareRes(id){
                let removeIndex = this.shareResources.indexOf(id);
                this.shareResources.splice(removeIndex, 1);
            },
            setCrisisFilters(){
                this.selectedCategories = _.filter(this.categories, (cat)=>{
                    return cat.idCategory === 23;
                })
            },
            copyShareUrl(){
                this.$refs.shareUrlInput.focus()
                this.$refs.shareUrlInput.select()
                document.execCommand("copy");
            },
            setupGTListener(){
                let gtEl = document.querySelector("#google_translate_element select.goog-te-combo")
                if(gtEl){
                    gtEl.addEventListener("change", this.updateGTLang);
                } else {
                    setTimeout(this.setupGTListener, 100);
                }
            },
            updateGTLang(e){
                let gtVal = e.target.value;
                if(gtVal !== ""){
                    this.shareTranslateString = "#googtrans(en|" + gtVal + ")";
                } else {
                    this.shareTranslateString = "";
                }
            }
        }
    }
</script>

<style lang="scss">

</style>